import React, { useEffect, useState } from 'react';
import Styled from './styled';
import { CloseIcon } from '../Icon';

export type NotificationCardProps = {
    /** accessibility support (alt text/aria-label, defaults to 'close') */
    ariaLabelClose?: string;
    /** accessibility support (alt text/aria-label, defaults to 'open') */
    ariaLabelOpen?: string;
    /** Notification text to be displayed. */
    notificationText?: string;
    /** Text for the expand link. */
    expandText?: string;
    /** Optional Click handler for the expand link, expand link only displays when prop is present */
    expandClick?: () => void;
    /** Whether the notification card is visible. */
    visible?: boolean;
    /** Timer in seconds for how long the notification card should be displayed. */
    timer?: number;
    /** Type of Notification Card (style)*/
    type?: 'default' | 'positive' | 'negative';

};

/**
 * NotificationCard component that displays a notification message with an expand link and a close button.
 * The notification can be automatically hidden after a specified timer.
 * 
 * @param {NotificationCardProps} props - The properties to configure the NotificationCard component.
 * @returns {JSX.Element | null} The rendered NotificationCard component or null if not visible.
 */
export const NotificationCard: React.FC<NotificationCardProps> = ({
    ariaLabelClose = 'close',
    ariaLabelOpen = 'open',
    notificationText = 'New Message',
    expandText = 'Click to expand',
    expandClick,
    visible = true,
    timer = 0, // Timer in seconds
    type = 'default',
}) => {
    const [isNotificationVisible, setNotificationVisible] = useState(visible);

    // Effect to handle the timer for auto-hiding the notification
    useEffect(() => {
        if (timer > 0 && isNotificationVisible) {
            const timeout = setTimeout(() => {
                setNotificationVisible(false);
            }, timer * 1000); // Convert seconds to milliseconds

            return () => clearTimeout(timeout);
        }
    }, [timer, isNotificationVisible]);

    const handleCloseButtonClick = () => {
        setNotificationVisible(false);
    };

    // Render null if the notification is not visible
    if (!isNotificationVisible) {
        return null;
    }

    return (
        <Styled.NotificationCard
            type={type}
            isVisible={isNotificationVisible}
        >
            <Styled.Header>
                <Styled.HeaderText>{notificationText}</Styled.HeaderText>
                <Styled.CloseButton
                    onClick={handleCloseButtonClick}
                    aria-label={ariaLabelClose}
                    tabIndex={0}
                    role='button'
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault(); // Prevent default scrolling for Space key
                            handleCloseButtonClick();
                        }
                    }}
                >
                    <CloseIcon />
                </Styled.CloseButton>
            </Styled.Header>
            {expandClick &&
                <Styled.ExpandLink
                    onClick={() => {
                        expandClick();
                        setNotificationVisible(false);
                    }}
                    aria-label={ariaLabelOpen}
                    tabIndex={0}
                    role='button'
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault(); // Prevent default scrolling for Space key
                            expandClick();
                            setNotificationVisible(false);
                        }
                    }}
                >
                    {expandText}
                </Styled.ExpandLink>
            }
        </Styled.NotificationCard>
    );
};
